<script>
import metricComponentsMixin from '../../mixins/metricComponentsMixin.js';
import InboxName from 'dashboard/components/widgets/InboxName.vue';
import { useAlert } from 'dashboard/composables';

import { formatTime } from '@chatwoot/utils';

// Display components
import SummaryIndicator from '../../displayComponents/Summary.vue';
import DonutChart from './Donut.vue';
import ColumnChart from './ColumnChart.vue';

// POSSIBILIDADES DE DADOS PARA MÉTRICA DE CONVERSAS
// eslint-disable-next-line no-unused-vars
const REPORTS_KEYS = {
  CONVERSATIONS: 'conversations_count',
  FIRST_RESPONSE_TIME: 'avg_first_response_time',
  RESOLUTION_TIME: 'avg_resolution_time',
  RESOLUTION_COUNT: 'resolutions_count',
  // REPLY_TIME: 'reply_time',
};

export default {
  components: {
    SummaryIndicator,
    InboxName,
    DonutChart,
    ColumnChart,
  },
  mixins: [metricComponentsMixin],
  props: {
    id: {
      type: String,
      required: true,
    },
    display: {
      type: Object,
      required: true,
    },
    // Current cell size
    sizeX: {
      type: Number,
      required: true,
    },
    sizeY: {
      type: Number,
      required: true,
    },
    dateRange: {
      type: Object,
      required: true,
    },
    inbox: {
      type: Object,
      default: () => ({}),
    },
    widgetVisualizationOptions: {
      type: Object,
      default: () => ({}),
    },
    submetric: {
      type: Object,
      default: () => ({}),
    },
    /**
     * Caso seja preciso acessar algum dado externo devemos utilizar props
     * Essas props devem ser declaradas dentro de 'contentProps' nos componentes pai:
     *    - CreateCustomDashboards
     *    - EditCustomDashboard
     * Também é possível adicionar no EditWidgetModal.vue, basta acrescentar o valor como parte do objeto que é enviado para a função 'edit-current-widget'
     */
  },
  data() {
    const translatedDateRangeOptions = this.translatedDateRangeOptions();

    return {
      selectedDateRange: translatedDateRangeOptions[0],
      options: translatedDateRangeOptions,
      selectedSubmetric: this.translatedSubmetricsOptions()[0],

      editingWidget: false, // Customer is on '/edit' page and is editing current widget

      isLoading: true,
      errorInReq: false,
      // SUMMARY (CARD) DATA
      conversationsCount: 0,
      resolutionsCount: 0,
      avgFirstResponseTime: 0,
      avgResolutionTime: 0,
      // COLUMNS CHART DATA
      accountReport: {
        submetric: '',
        data: {},
      },

      shouldShowPreview: false,

      componentWidth: 0,
    };
  },
  computed: {
    summaryContent() {
      let summaryContent;
      const { type } = this.submetric;

      if (type === 'resolutions_count') summaryContent = this.resolutionsCount;
      else if (type === 'avg_first_response_time')
        summaryContent = formatTime(this.avgFirstResponseTime);
      else if (type === 'avg_resolution_time')
        summaryContent = formatTime(this.avgResolutionTime);
      else summaryContent = this.conversationsCount;

      return summaryContent.toLocaleString();
    },
    donutData() {
      return {
        outgoing: this.outgoingCount || 0,
        incoming: this.incomingCount || 0,
      };
    },
    widgetContentHeightClass() {
      // Checking if widget is displaying the descriptions
      const { metric, dateRange, inbox, submetric } =
        this.widgetVisualizationOptions;
      const showingDescriptions = Boolean(
        metric || dateRange || (inbox && this.inbox?.id) || submetric
      );
      return showingDescriptions ? 'h-[calc(100%-40px)]' : 'h-full';
    },
  },
  mounted() {},
  methods: {
    setInitialData() {
      const { type: dateRangeType } = this.dateRange;
      this.selectedDateRange = this.translatedDateRangeOptions().find(
        e => e.id === dateRangeType
      );

      const { type: submetricType } = this.submetric;
      this.selectedSubmetric = this.translatedSubmetricsOptions().find(
        e => e.type === submetricType
      );
    },

    // Essa função é a responsável por chamar a função de request correta de acordo com o tipo de dado que precisamos
    // Exemplo: O Summary pode precisar de um valor (retorno de request) diferente de um gráfico de barras
    async fetchData() {
      if (this.isSummaryDisplay) await this.fetchSummaryAccountData();
      if (this.isColumnChartDisplay) await this.fetchDataWithinAccountReport();
      // if (this.isDonutDisplay) await this.fetchSummaryAccountData();

      // Se for outros displays adicionar aqui as condições
      this.isLoading = false;
    },

    showPreview() {
      this.shouldShowPreview = true;
      this.fetchData();
    },
    // SUMMARY (CARD) DATA
    async fetchSummaryAccountData() {
      const payload = this.getRequestPayload();
      if (this.inbox.id) {
        payload.type = 'inbox';
        payload.id = this.inbox.id;
      }
      try {
        const data = await this.$store.dispatch('fetchAccountSummary', payload);
        this.conversationsCount = data.conversations_count;
        this.resolutionsCount = data.resolutions_count;
        this.avgFirstResponseTime = data.avg_first_response_time;
        this.avgResolutionTime = data.avg_resolution_time;
      } catch {
        useAlert(this.$t('REPORT.DATA_FETCHING_FAILED'));
        this.errorInReq = true;
      }
    },
    // COLUMNS CHART DATA
    async fetchDataWithinAccountReport() {
      const key = this.submetric.type;
      const payload = {
        metric: key,
        ...this.getRequestPayload(),
        ...(this.inbox.id && { type: 'inbox', id: this.inbox.id }),
      };

      try {
        const data = await this.$store.dispatch('fetchAccountReport', payload);
        this.accountReport = {
          submetric: key,
          data,
        };
      } catch {
        useAlert(this.$t('REPORT.DATA_FETCHING_FAILED'));
      }
    },
  },
};
</script>

<template>
  <div
    ref="widget"
    class="max-h-full h-full overflow-auto pt-3 px-3 widget-container"
  >
    <div class="w-full flex relative">
      <div class="flex gap-1 max-w-[calc(100%-40px)] flex-wrap">
        <!-- NAME OF SELECTED METRIC -->
        <div
          v-if="widgetVisualizationOptions.metric"
          class="widget_preferences_tags bg-slate-50 border-slate-50"
        >
          {{ $t('CUSTOM_DASHBOARDS.WIDGET_TYPES.CONVERSATIONS') }}
        </div>
        <!-- NAME OF SELECTED SUB METRIC -->
        <div
          v-if="widgetVisualizationOptions.submetric"
          class="widget_preferences_tags bg-woot-50 border-woot-50"
        >
          {{ selectedSubmetric.title }}
        </div>
        <!-- SELECTED DATE RANGE -->
        <div
          v-if="widgetVisualizationOptions.dateRange"
          class="widget_preferences_tags bg-woot-50 border-woot-50"
        >
          <span class="text-[black]">
            {{ $t('CUSTOM_DASHBOARDS.EDIT_WIDGET.INTERVAL') }}
          </span>
          &nbsp;{{ dateRangeDescription }}
        </div>
        <!-- INBOX NAME (IF SELECTED) -->
        <div
          v-if="inbox.id && widgetVisualizationOptions.inbox"
          class="widget_preferences_tags bg-yellow-100 border-yellow-100"
        >
          <InboxName
            :inbox="currentInbox"
            :text-length="30"
            class="ltr:ml-0 rtl:mr-0 mb-0 m-0 mr-0 text-slate-800 text-xxs"
          />
        </div>
      </div>

      <!-- BUTTONS EDIT/SAVE WIDGET SECTION -->
      <div v-if="canEdit" class="z-[999] absolute right-0">
        <!-- BUTTON TO OPEN EDITING SECTION -->
        <woot-button
          class="flex items-center"
          size="tiny"
          variant="smooth"
          color-scheme="secondary"
          icon="settings"
          @click="openEditModal"
        />
      </div>
    </div>

    <div
      class="w-full pb-3 flex justify-center items-center"
      :class="widgetContentHeightClass"
    >
      <!-- 
     Adicionando botão de prévia para evitar requisições desnecessárias.
     A requisição é feita apenas quando o cliente exibe o display (não está editando/criando).
     Isso resolve o problema de múltiplas requisições desnecessário ao modificar um único widget -->
      <woot-button v-if="canEdit && !shouldShowPreview" @click="showPreview">
        {{ $t('CUSTOM_DASHBOARDS.EDIT_WIDGET.VIEW_PREVIEW') }}
      </woot-button>
      <div v-else>
        <!-- SPINNER WAITING REQUEST FINISHES -->
        <woot-loading-state
          v-if="isLoading"
          class="text-xxs"
          :message="$t('REPORT.LOADING_CHART')"
        />
        <!-- WHEN REQUISITION UNSUCCESSFUL -->
        <div v-else-if="errorInReq" class="text-red-600 text-xs">
          ⚠️ {{ $t('REPORT.DATA_FETCHING_FAILED') }}
        </div>
        <!-- DATA BEING DISPLAYED -->
        <div v-else>
          <SummaryIndicator
            v-if="isSummaryDisplay"
            :size-x="sizeX"
            :size-y="sizeY"
            :content="summaryContent"
          />
          <DonutChart
            v-else-if="isDonutDisplay"
            :size-x="sizeX"
            :size-y="sizeY"
            :current-width="componentWidth"
            :data="donutData"
          />
          <ColumnChart
            v-else-if="isColumnChartDisplay"
            :size-x="sizeX"
            :size-y="sizeY"
            :current-width="componentWidth"
            :group-by="groupBy"
            :data="accountReport"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style>
.widget-container > div {
  @apply text-slate-700;
}
.widget_preferences_tags {
  @apply text-xxs leading-none p-2 rounded-full text-center flex justify-center items-center border border-solid;
}
</style>
